import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaUser, FaPhone, FaEnvelope, FaCarSide, FaWrench,
  FaCalendarCheck, FaArrowLeft, FaCheckCircle,
} from 'react-icons/fa';
import { useApi } from '../../api';
import { formatearFechaLocal } from '../utils';
import logo from '../assets/logo_anduze.png';
import fondoVideo from '../assets/login2.mp4';

const CAMPOS_INICIALES = {
  nombre_cliente: '',
  telefono: '',
  correo: '',
  placa: '',
  marca_modelo: '',
  motivo: '',
};

function hoyISO() {
  return formatearFechaLocal();
}

const AgendarCita = () => {
  const api = useApi();
  const [fecha, setFecha] = useState(hoyISO());
  const [slots, setSlots] = useState([]);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [hora, setHora] = useState(null);
  const [form, setForm] = useState(CAMPOS_INICIALES);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [confirmacion, setConfirmacion] = useState(null);

  const cargarSlots = async (fechaConsulta) => {
    setCargandoSlots(true);
    setHora(null);
    await refrescarSlots(fechaConsulta);
    setCargandoSlots(false);
  };

  // A diferencia de cargarSlots, no resetea la hora ni el paso actual:
  // se usa para refrescar la disponibilidad sin sacar al usuario del
  // formulario cuando su franja elegida ya no está disponible.
  const refrescarSlots = async (fechaConsulta) => {
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/citas/disponibilidad/`, {
        params: { fecha: fechaConsulta },
      });
      setSlots(res.data.slots || []);
    } catch (err) {
      console.error('Error al cargar disponibilidad', err);
      setSlots([]);
    }
  };

  useEffect(() => {
    if (fecha) cargarSlots(fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hora) return;
    setEnviando(true);
    setError('');
    try {
      const res = await api.post(`${process.env.REACT_APP_API_URL}/api/citas/`, {
        ...form,
        fecha,
        hora,
      });
      setConfirmacion(res.data);
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        setError(Object.values(data).flat().join(' ') || 'No se pudo agendar la cita.');
      } else {
        setError('No se pudo agendar la cita. Intenta de nuevo.');
      }
      refrescarSlots(fecha);
    } finally {
      setEnviando(false);
    }
  };

  const fechaBonita = new Date(`${fecha}T00:00:00`).toLocaleDateString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  return (
    <>
      {/* Video de fondo */}
      <video
        autoPlay loop muted playsInline
        style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', objectFit: 'cover', zIndex: -2 }}
      >
        <source src={fondoVideo} type="video/mp4" />
      </video>

      {/* Overlay oscuro sobre el video */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: -1,
        background: 'linear-gradient(135deg, rgba(8,20,60,0.78) 0%, rgba(8,58,148,0.6) 100%)',
      }} />

      {/* Acceso del taller */}
      <Link
        to="/login"
        style={{
          position: 'fixed', top: 18, right: 20, zIndex: 5,
          color: 'rgba(255,255,255,0.75)', fontSize: 13, fontFamily: 'Poppins, sans-serif',
          textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: 20, padding: '6px 14px', backdropFilter: 'blur(6px)',
          background: 'rgba(255,255,255,0.06)', transition: 'background 0.2s',
        }}
        onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.16)')}
        onMouseOut={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
      >
        ¿Eres del taller? Ingresar
      </Link>

      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', fontFamily: 'Poppins, Segoe UI, sans-serif',
        padding: '3.2rem 1rem 3.5rem',
      }}>
        {/* Logo y encabezado */}
        <motion.img
          src={logo}
          alt="Autocars Anduze"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          style={{ width: 130, marginBottom: 14, filter: 'drop-shadow(0 4px 18px rgba(0,0,0,0.35))' }}
        />
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{
            color: '#fff', fontWeight: 800, fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
            letterSpacing: '0.03em', margin: 0, textAlign: 'center',
          }}
        >
          Agenda tu cita en el taller
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: '6px 0 28px', textAlign: 'center' }}
        >
          Reserva en minutos, sin llamadas ni filas. Te enviamos la confirmación a tu correo.
        </motion.p>

        {/* Card principal */}
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, type: 'spring', bounce: 0.18 }}
          style={{
            width: '100%', maxWidth: 520,
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            borderRadius: 28,
            border: '1.5px solid rgba(255,255,255,0.22)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
            padding: '2rem 1.8rem',
          }}
        >
          <AnimatePresence mode="wait">
            {confirmacion ? (
              <motion.div
                key="confirmacion"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{ textAlign: 'center', color: '#fff' }}
              >
                <FaCheckCircle style={{ fontSize: 46, color: '#5ce18b', marginBottom: 12 }} />
                <h3 style={{ fontWeight: 700, margin: '0 0 10px' }}>¡Cita agendada!</h3>
                <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>
                  Te esperamos el <strong>{fechaBonita}</strong> a las <strong>{hora}</strong>.
                </p>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13.5 }}>
                  Revisa tu correo: te enviamos la confirmación con un archivo para
                  guardar la cita en el calendario de tu celular.
                </p>
                {confirmacion.email_advertencia && (
                  <p style={{ color: '#ffd166', fontSize: 13 }}>⚠️ {confirmacion.email_advertencia}</p>
                )}
              </motion.div>
            ) : !hora ? (
              <motion.div
                key="slots"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
              >
                <label style={dLabelStyle}>
                  <FaCalendarCheck style={{ marginRight: 7 }} /> Elige una fecha
                </label>
                <input
                  type="date"
                  className="cita-date-input"
                  min={hoyISO()}
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  style={{ ...inputBase, marginBottom: 18 }}
                />

                <label style={dLabelStyle}>Horarios disponibles</label>
                {cargandoSlots && (
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13.5, padding: '8px 0' }}>
                    Cargando horarios...
                  </div>
                )}
                {!cargandoSlots && slots.length === 0 && (
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13.5, padding: '8px 0' }}>
                    No hay horarios disponibles para este día. Prueba con otra fecha.
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                  {slots.map((slot) => (
                    <button
                      type="button"
                      key={slot.hora}
                      disabled={!slot.disponible}
                      onClick={() => setHora(slot.hora)}
                      style={slotButtonStyle(slot.disponible)}
                    >
                      {slot.hora}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
              >
                <button
                  type="button"
                  onClick={() => setHora(null)}
                  style={backLinkStyle}
                >
                  <FaArrowLeft size={12} /> {fechaBonita} · {hora}
                </button>

                <CampoIcono icon={<FaUser />} name="nombre_cliente" placeholder="Nombre completo"
                  value={form.nombre_cliente} onChange={handleFormChange} />
                <CampoIcono icon={<FaPhone />} name="telefono" placeholder="Teléfono"
                  value={form.telefono} onChange={handleFormChange} />
                <CampoIcono icon={<FaEnvelope />} name="correo" type="email" placeholder="Correo electrónico"
                  value={form.correo} onChange={handleFormChange} />
                <CampoIcono icon={<FaCarSide />} name="placa" placeholder="Placa del vehículo"
                  value={form.placa} onChange={handleFormChange} />
                <CampoIcono icon={<FaCarSide />} name="marca_modelo" placeholder="Marca / modelo (opcional)"
                  value={form.marca_modelo} onChange={handleFormChange} required={false} />

                <div style={{ position: 'relative', marginBottom: 14 }}>
                  <FaWrench style={{ position: 'absolute', top: 16, left: 14, color: 'rgba(255,255,255,0.5)', fontSize: 14 }} />
                  <textarea
                    name="motivo"
                    placeholder="Motivo de la visita"
                    required
                    rows={3}
                    value={form.motivo}
                    onChange={handleFormChange}
                    style={{ ...inputBase, paddingTop: 12, paddingLeft: 40, resize: 'vertical' }}
                  />
                </div>

                {error && (
                  <div style={{
                    color: '#ff6b6b', fontSize: 13, marginBottom: 12, textAlign: 'center',
                  }}>
                    ✕ {error}
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={enviando}
                  whileHover={{ scale: 1.02, boxShadow: '0 8px 28px rgba(25,118,210,0.45)' }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 14, border: 'none',
                    background: enviando
                      ? 'rgba(25,118,210,0.5)'
                      : 'linear-gradient(90deg, #1565c0 0%, #1e88e5 100%)',
                    color: '#fff', fontWeight: 700, fontSize: 15, letterSpacing: '0.03em',
                    cursor: enviando ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {enviando ? 'Agendando...' : 'Confirmar cita'}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      <style>{`
        .cita-date-input::-webkit-calendar-picker-indicator {
          filter: invert(1);
          cursor: pointer;
        }
      `}</style>

      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        style={{
          position: 'fixed', bottom: 0, left: 0, width: '100%',
          textAlign: 'center', padding: '10px 0', zIndex: 99,
          fontSize: 12, color: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          background: 'rgba(0,0,0,0.15)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        <a
          href="https://www.effsos.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none', transition: 'color 0.2s' }}
          onMouseOver={e => (e.target.style.color = '#5bc0f8')}
          onMouseOut={e => (e.target.style.color = 'rgba(255,255,255,0.55)')}
        >
          EFFSOS – Soluciones de Software Empresarial © {new Date().getFullYear()} · v0.10.4
        </a>
      </motion.footer>
    </>
  );
};

const dLabelStyle = {
  display: 'block', color: 'rgba(255,255,255,0.85)', fontSize: 13.5,
  fontWeight: 600, marginBottom: 8,
};

const inputBase = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 12,
  border: '1.5px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.08)',
  color: '#fff',
  fontSize: 14,
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
  colorScheme: 'dark',
};

const backLinkStyle = {
  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
  background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
  fontSize: 13, fontFamily: 'Poppins, sans-serif', cursor: 'pointer', padding: 0,
};

const slotButtonStyle = (disponible) => ({
  padding: '8px 14px',
  borderRadius: 10,
  fontSize: 13.5,
  fontWeight: 600,
  fontFamily: 'Poppins, sans-serif',
  border: '1px solid rgba(255,255,255,0.25)',
  background: disponible ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.03)',
  color: disponible ? '#fff' : 'rgba(255,255,255,0.3)',
  cursor: disponible ? 'pointer' : 'not-allowed',
  transition: 'background 0.15s, transform 0.15s',
});

function CampoIcono({ icon, name, type = 'text', placeholder, value, onChange, required = true }) {
  return (
    <div style={{ position: 'relative', marginBottom: 12 }}>
      <span style={{
        position: 'absolute', top: '50%', left: 14, transform: 'translateY(-50%)',
        color: 'rgba(255,255,255,0.5)', fontSize: 14, display: 'flex',
      }}>
        {icon}
      </span>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
        value={value}
        onChange={onChange}
        style={{ ...inputBase, paddingLeft: 40 }}
      />
    </div>
  );
}

export default AgendarCita;
